import type { KeyboardEvent } from 'react'

import { cn, numberToCurrency } from '@/utils'
import { moneyMovementCount } from '../model/activityView'
import type { AccountAsOf } from '../model/portfolioOverview'
import { countOf, monthSpanText } from '../portfolioText'
import { VerdictBadge } from '../VerdictBadge'
import { CELL_CLASS, FigureCell, TwoLineCell } from './cells'
import { noValueText } from './noValueText'

interface AccountRowProps {
  account: AccountAsOf
  inGroup: boolean
  showChange: boolean
  isSelected: boolean
  onSelect: (accountId: string) => void
}

const statementSpan = (account: AccountAsOf) =>
  account.firstStatementMonth && account.lastStatementMonth
    ? monthSpanText(account.firstStatementMonth, account.lastStatementMonth)
    : undefined

const hasMoneyMovements = (account: AccountAsOf) =>
  account.money.totals != null && moneyMovementCount(account.money.totals) > 0

const ValueCells = ({
  account,
  showChange,
}: {
  account: AccountAsOf
  showChange: boolean
}) => {
  const noValue = noValueText(account)
  if (noValue) {
    return (
      <FigureCell quiet colSpan={showChange ? 2 : 1}>
        {noValue}
      </FigureCell>
    )
  }
  return (
    <>
      <FigureCell>{numberToCurrency(account.value ?? 0)}</FigureCell>
      {showChange && (
        <FigureCell quiet={!account.change}>
          {account.change
            ? numberToCurrency(account.change.amount)
            : 'No earlier statement'}
        </FigureCell>
      )}
    </>
  )
}

const stopRowClick = (event: { stopPropagation: () => void }) =>
  event.stopPropagation()

export const AccountRow = ({
  account,
  inGroup,
  showChange,
  isSelected,
  onSelect,
}: AccountRowProps) => {
  const select = () => onSelect(account.accountId)
  const selectOnEnter = (event: KeyboardEvent) => {
    if (event.key === 'Enter') select()
  }
  const [firstReason] = account.badge.reasons

  return (
    <tr
      tabIndex={0}
      aria-selected={isSelected}
      onClick={select}
      onKeyDown={selectOnEnter}
      className={cn(
        'cursor-pointer [&>td:first-child]:rounded-l-md [&>td:last-child]:rounded-r-md',
        isSelected ? '[&>td]:bg-slate-100' : 'hover:[&>td]:bg-slate-50',
      )}
    >
      <TwoLineCell
        main={account.name}
        sub={account.accountType}
        className={cn(inGroup && 'pl-8')}
      />
      <TwoLineCell
        main={
          account.statementCount === 0
            ? 'No statements yet'
            : countOf(account.statementCount, 'statement')
        }
        sub={statementSpan(account)}
      />
      <ValueCells account={account} showChange={showChange} />
      <FigureCell quiet={!hasMoneyMovements(account)}>
        {hasMoneyMovements(account)
          ? numberToCurrency(account.money.netMoney)
          : 'None found'}
      </FigureCell>
      <td
        className={CELL_CLASS}
        onClick={stopRowClick}
        onKeyDown={stopRowClick}
      >
        {account.status !== 'before_first_statement' && (
          <VerdictBadge
            status={account.badge.status}
            reason={firstReason}
            moreReasons={Math.max(account.badge.reasons.length - 1, 0)}
          />
        )}
      </td>
    </tr>
  )
}
